import React, { FC, ReactNode, useEffect, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import {
  type Account,
  AccountsResponse,
  useAccountEntitlementsStore,
} from './stores/AccountEntitlementsStore';

export type { Account } from './stores/AccountEntitlementsStore';

type AccountEntitlementsContextValue = {
  data: null | AccountsResponse;
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setAccounts(data: AccountEntitlementsContextValue['data']): void;
  updateAccount(account: Account): void;
  addAccount(account: Account): void;
};

type AccountEntitlementsProviderProps = {
  disabled?: boolean;
  children?: ReactNode;
};

/**
 * Context for account entitlements.
 */
export const AccountEntitlementsContext =
  getOrCreateGlobalContext<AccountEntitlementsContextValue>(
    CONTEXT_NAMES.AccountEntitlementsContext,
  );

/**
 * Returns the full account entitlements context value. Must be used inside `AccountEntitlementsProvider`.
 */
export const useAccountEntitlements = () =>
  useStoreWithValidation<AccountEntitlementsContextValue>(
    CONTEXT_NAMES.AccountEntitlementsContext,
    {
      hook: 'useAccountEntitlements',
      provider: 'AccountEntitlementsProvider',
      context: AccountEntitlementsContext,
    },
  );

/**
 * This hook returns the current Account based on the current Account entitlements response.
 * Returns undefined if we don't have Account entitlement.
 * Must be used inside both `AccountEntitlementsProvider` and `CurrentAccountIdProvider`.
 *
 * @returns {undefined|object}
 */
export const useCurrentAccount = () => {
  const accountId = useCurrentAccountId();
  const { data: accountEntitlements } = useAccountEntitlements();
  return accountEntitlements?.accounts.find(
    (account) => account?.account_id === accountId,
  );
};

export const useAccountEntitlementsProvider = (disabled?: boolean) => {
  const {
    data,
    error,
    loading,
    fetchData,
    retry,
    setAccounts,
    addAccount,
    updateAccount,
  } = useAccountEntitlementsStore();

  useEffect(() => {
    if (!disabled) {
      fetchData();
    }
  }, [disabled, fetchData]);

  return useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addAccount,
      updateAccount,
      setAccounts,
    }),
    [data, loading, error, retry, addAccount, updateAccount, setAccounts],
  );
};

export const AccountEntitlementsProvider: FC<
  AccountEntitlementsProviderProps
> = ({ children, disabled }) => {
  const accountEntitlementsContextValue =
    useAccountEntitlementsProvider(disabled);

  useProviderOnlyOnce({
    context: AccountEntitlementsContext,
    contextName: CONTEXT_NAMES.AccountEntitlementsContext,
  });

  return (
    <AccountEntitlementsContext.Provider
      value={accountEntitlementsContextValue}
    >
      {children}
    </AccountEntitlementsContext.Provider>
  );
};

AccountEntitlementsProvider.displayName = 'AccountEntitlementsProvider';
